import { useGetDealerBrandsQuery } from '@api/customer.api';
import { WarrantyClaimStatus, WarrantyClaimType } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { useGetWarrantyClaimsProductFiltersQuery } from '@api/warranty.api';
import { CalendarField, DropdownField, SearchField } from '@components';
import { useAuth } from '@hooks';
import { Clear } from '@mui/icons-material';
import { Box, Button, Grid } from '@mui/material';
import { t } from '@translations';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { WarrantyFilters, useWarrantyListContext } from './WarrantyListContext';

export const WarrantyListFilters = () => {
  const { isBeremaUser } = useAuth();
  const { filters, updateFilters, clearFilters } = useWarrantyListContext();
  const { currentData: brandsData } = useGetDealerBrandsQuery();
  const { currentData: productOptions } = useGetWarrantyClaimsProductFiltersQuery();
  const prevProductTypeRef = useRef(filters?.productType);

  const brands = brandsData?.data?.map((brand) => ({
    label: brand.label,
    value: brand.label,
  }));

  const statuses = [
    {
      label: t(TextCodes.Warranty.Status.New),
      value: WarrantyClaimStatus.New,
    },
    {
      label: t(TextCodes.Warranty.Status.Accepted),
      value: WarrantyClaimStatus.Accepted,
    },
    {
      label: t(TextCodes.Warranty.Status.Rejected),
      value: WarrantyClaimStatus.Rejected,
    },
    {
      label: t(TextCodes.Warranty.Status.Returned),
      value: WarrantyClaimStatus.Returned,
    },
    ...(isBeremaUser
      ? [
          {
            label: t(TextCodes.Warranty.Status.SentToVendor),
            value: WarrantyClaimStatus.SentToVendor,
          },
        ]
      : []),
    {
      label: t(TextCodes.Warranty.Status.Credited),
      value: WarrantyClaimStatus.Credited,
    },
    ...(isBeremaUser
      ? [
          {
            label: t(TextCodes.Warranty.Status.SentToVendorAndCredited),
            value: WarrantyClaimStatus.SentToVendorAndCredited,
          },
        ]
      : []),
  ];

  const claimTypes = [
    {
      label: t(TextCodes.Warranty.Block.Warranty),
      value: WarrantyClaimType.Warranty,
    },
    {
      label: t(TextCodes.Warranty.Block.Complaint),
      value: WarrantyClaimType.Complaint,
    },
    {
      label: t(TextCodes.Warranty.Block.Goodwill),
      value: WarrantyClaimType.Goodwill,
    },
  ];

  const findFieldValues = useCallback(
    (filterName: keyof WarrantyFilters) => {
      return filters?.[filterName] || '';
    },
    [filters]
  );

  const productTypeOptions = useMemo(() => {
    return (
      productOptions
        ?.filter((item) => item.productType)
        .map((item) => ({
          label: item.productType as string,
          value: item.productType as string,
        })) || []
    );
  }, [productOptions]);

  const productModelsOptions = useMemo(() => {
    return (
      productOptions
        ?.find((option) => option.productType === findFieldValues('productType'))
        ?.productModels?.filter((model): model is string => model !== null)
        ?.map((model) => ({ label: model, value: model })) || []
    );
  }, [productOptions, findFieldValues]);

  useEffect(() => {
    if (
      prevProductTypeRef.current !== undefined &&
      prevProductTypeRef.current !== filters?.productType
    ) {
      updateFilters({ filterName: 'productModel', value: undefined });
    }
    prevProductTypeRef.current = filters?.productType;
  }, [filters?.productType, updateFilters]);

  useEffect(() => {
    if (filters?.salesDateEnd && !filters?.salesDateStart) {
      updateFilters({ filterName: 'salesDateEnd', value: undefined });
    }
  }, [filters?.salesDateStart, updateFilters, filters?.salesDateEnd]);

  useEffect(() => {
    if (filters?.repairDateEnd && !filters?.repairDateStart) {
      updateFilters({ filterName: 'repairDateEnd', value: undefined });
    }
  }, [filters?.repairDateStart, updateFilters, filters?.repairDateEnd]);

  return (
    <Box sx={{ my: 4 }}>
      <Grid
        container
        rowSpacing={4}
        columnSpacing={2}
        sx={{
          mb: 4,
        }}
      >
        <Grid item xs={12} sm={12} md={12} lg={5}>
          <Grid container rowSpacing={4} columnSpacing={2}>
            <Grid item xs={12} sm={6} md={6}>
              <DropdownField
                label={t(TextCodes.Warranty.Block.ProductType)}
                name='productType'
                getValue={(value) => {
                  updateFilters({ filterName: 'productType', value: value as string });
                }}
                value={findFieldValues('productType')}
                options={productTypeOptions || []}
                withRemoveButton
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <DropdownField
                label={t(TextCodes.Warranty.Block.ProductModel)}
                name='productModel'
                getValue={(value) => {
                  updateFilters({ filterName: 'productModel', value: value as string });
                }}
                value={findFieldValues('productModel')}
                options={productModelsOptions}
                withRemoveButton
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={7}>
          <Grid container rowSpacing={4} columnSpacing={2}>
            <Grid item xs={12} sm={4} md={4}>
              <DropdownField
                label={t(TextCodes.Warranty.Block.Brand)}
                name='brand'
                getValue={(value) => {
                  updateFilters({ filterName: 'brand', value: value as string });
                }}
                value={findFieldValues('brand')}
                options={brands || []}
                withRemoveButton
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <DropdownField
                name='statuses'
                label={t(TextCodes.Warranty.Block.Status)}
                getValue={(value) => {
                  updateFilters({ filterName: 'statuses', value });
                }}
                value={findFieldValues('statuses')}
                options={statuses || []}
                multiple
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <DropdownField
                name='claimType'
                label={t(TextCodes.Warranty.Block.ClaimType)}
                getValue={(value) => {
                  updateFilters({ filterName: 'claimType', value: value as string });
                }}
                value={findFieldValues('claimType')}
                options={claimTypes || []}
                withRemoveButton
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container rowSpacing={4} columnSpacing={2}>
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <SearchField
                label={t(TextCodes.Warranty.Block.Id)}
                name='id'
                sx={{ width: '100%' }}
                value={findFieldValues('id')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateFilters({ filterName: 'id', value: e.target.value })
                }
              />
            </Grid>
            {isBeremaUser && (
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <SearchField
                  name='customerNo'
                  value={findFieldValues('customerNo')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFilters({ filterName: 'customerNo', value: e.target.value })
                  }
                  sx={{ width: '100%' }}
                  label={t(TextCodes.Warranty.Block.CustomerNo)}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <SearchField
                label={t(TextCodes.Warranty.Block.SerialNo)}
                name='serialNo'
                value={findFieldValues('serialNo')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateFilters({ filterName: 'serialNo', value: e.target.value })
                }
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <SearchField
                label={t(TextCodes.Warranty.Block.PartNo)}
                name='partNo'
                value={findFieldValues('partNo')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateFilters({ filterName: 'partNo', value: e.target.value })
                }
                sx={{ width: '100%' }}
              />
            </Grid>
            {!isBeremaUser && (
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <SearchField
                  label={t(TextCodes.Warranty.Block.EngineNo)}
                  name='engineNumber'
                  value={findFieldValues('engineNumber')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFilters({ filterName: 'engineNumber', value: e.target.value })
                  }
                  sx={{ width: '100%' }}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        {isBeremaUser && (
          <Grid item xs={12} sm={6} md={3} lg={3}>
            <SearchField
              label={t(TextCodes.Warranty.Block.EngineNo)}
              name='engineNumber'
              value={findFieldValues('engineNumber')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateFilters({ filterName: 'engineNumber', value: e.target.value })
              }
              sx={{ width: '100%' }}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={3} lg={2}>
          <CalendarField
            value={findFieldValues('salesDateStart')}
            onChange={(e?: string) => {
              if (!e) {
                updateFilters({ filterName: 'salesDateStart', value: undefined });
                return;
              }
              updateFilters({
                filterName: 'salesDateStart',
                value: dayjs(e).format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
              });
            }}
            clearable
            label={t(TextCodes.Warranty.Block.SaleDateStart)}
            placeholder='DD/MM/YYYY'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={2}>
          <CalendarField
            value={findFieldValues('salesDateEnd')}
            clearable
            onChange={(e?: string) => {
              if (!e) {
                updateFilters({ filterName: 'salesDateEnd', value: undefined });
                return;
              }
              updateFilters({
                filterName: 'salesDateEnd',
                value: dayjs(e).format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
              });
            }}
            label={t(TextCodes.Warranty.Block.SaleDateEnd)}
            placeholder='DD/MM/YYYY'
            minDate={
              findFieldValues('salesDateEnd')
                ? dayjs(findFieldValues('salesDateEnd')).toDate().toString()
                : undefined
            }
            disabled={!filters?.salesDateStart}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={2}>
          <CalendarField
            value={findFieldValues('repairDateStart')}
            clearable
            onChange={(e?: string) => {
              if (!e) {
                updateFilters({ filterName: 'repairDateStart', value: undefined });
                return;
              }
              updateFilters({
                filterName: 'repairDateStart',
                value: dayjs(e).format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
              });
            }}
            label={t(TextCodes.Warranty.Block.RepairDateStart)}
            placeholder='DD/MM/YYYY'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={2}>
          <CalendarField
            value={findFieldValues('repairDateEnd')}
            clearable
            onChange={(e?: string) => {
              if (!e) {
                updateFilters({ filterName: 'repairDateEnd', value: undefined });
                return;
              }
              updateFilters({
                filterName: 'repairDateEnd',
                value: dayjs(e).format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
              });
            }}
            label={t(TextCodes.Warranty.Block.RepairDateEnd)}
            placeholder='DD/MM/YYYY'
            minDate={
              findFieldValues('repairDateStart')
                ? dayjs(findFieldValues('repairDateStart')).toDate().toString()
                : undefined
            }
            disabled={!filters?.repairDateStart}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={1}>
          <Button variant='tertiary' onClick={clearFilters} startIcon={<Clear />}>
            {t(TextCodes.TechnicalMessage.Block.ClearFilters)}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};
